import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useMedia } from "@/contexts/MediaContext";
import { useMobile } from "@/hooks/useMobile";
import { FileInputArea } from "./FileInputArea";
import { FileGridPreview } from "./FileGridPreview";
import { ObjectPromptCard } from "./ObjectPromptCard";
import { MediaItem } from "@/types/media";

interface HomeFileInputAreaProps {
    addMediaItems: (files: File[]) => void;
    erroredMediaItems?: MediaItem[];
    qrSize?: number;
    showAllMethods?: boolean;
    onSubmit: (textPrompts: string) => void;
}

const containerAnimation = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
    transition: { duration: 0.3, ease: "easeOut" }
};

export const HomeFileInputArea = ({
    addMediaItems,
    erroredMediaItems = [],
    qrSize = 64,
    showAllMethods = false,
    onSubmit
}: HomeFileInputAreaProps) => {
    const { mediaItems, orderedMediaItems } = useMedia();
    const [isHovered, setIsHovered] = useState(false);
    const [hasEverUploadedFiles, setHasEverUploadedFiles] = useState(false);
    const isMobile = useMobile();
    const hasFiles = mediaItems.length > 0;

    // Track if files have ever been uploaded
    useEffect(() => {
        if (hasFiles && !hasEverUploadedFiles) {
            setHasEverUploadedFiles(true);
        }
    }, [hasFiles, hasEverUploadedFiles]);

    const handleAddFilesInPreview = useCallback(
        async (files: File[]) => {
            await addMediaItems(files);
        },
        [addMediaItems]
    );

    return (
        <AnimatePresence mode="wait">
            <div className="transparentContainer h-96 items-stretch">
                <motion.div
                    key="file-section"
                    initial={false}
                    animate={{ flexGrow: hasEverUploadedFiles && !isMobile ? 1 : 2, flexShrink: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className={`flex h-full min-w-0 flex-1 overflow-hidden rounded-xl bg-white ${hasFiles ? "border border-gray-300" : ""}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {hasFiles && !isMobile ? (
                        <motion.div
                            key="file-grid-preview"
                            {...containerAnimation}
                            className="h-full w-full min-w-0"
                        >
                            <FileGridPreview
                                onAddFiles={handleAddFilesInPreview}
                                orderedMediaItems={orderedMediaItems}
                                isHomePage={true}
                            />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="file-input-area"
                            {...containerAnimation}
                            className="h-full w-full min-w-0"
                        >
                            <FileInputArea
                                addMediaItems={addMediaItems}
                                erroredMediaItems={erroredMediaItems}
                                qrSize={qrSize}
                                showAllMethods={showAllMethods}
                                externalIsHovered={isHovered}
                                hasEverUploadedFiles={hasEverUploadedFiles}
                            />
                        </motion.div>
                    )}
                </motion.div>
                {!isMobile && hasEverUploadedFiles && (
                    <motion.div
                        key="object-prompt-card"
                        initial={{ opacity: 0, flexGrow: 0, width: 0 }}
                        animate={{ opacity: 1, flexGrow: 1, width: "auto" }}
                        exit={{ opacity: 0, flexGrow: 0, width: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="flex-1 overflow-hidden"
                    >
                        <ObjectPromptCard onSubmit={onSubmit} />
                    </motion.div>
                )}
            </div>
        </AnimatePresence>
    );
};
